import { useEffect, useState } from 'react';
import { platformAnalytics } from '@/services/platform';
import styles from './AnalyticsPage.module.css';

interface DocumentStats {
  totalDocuments: number;
  documentsByType: { typeId: string; typeName: string; count: number }[];
  documentsByStatus: { statusId: string; statusName: string; count: number }[];
  documentsCreatedToday: number;
  documentsCreatedThisWeek: number;
  documentsCreatedThisMonth: number;
}

interface UserStats {
  totalUsers: number;
  activeUsers: number;
  usersByMda: { mdaId: string; mdaName: string; count: number }[];
  loginsToday: number;
  loginsThisWeek: number;
}

interface StorageStats {
  totalStorageUsedMb: number;
  storageByMda: { mdaId: string; mdaName: string; usedMb: number }[];
}

export function AnalyticsPage() {
  const [documentStats, setDocumentStats] = useState<DocumentStats | null>(null);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [storageStats, setStorageStats] = useState<StorageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'documents' | 'users' | 'storage'>('overview');

  useEffect(() => {
    async function fetchAnalytics() {
      setLoading(true);
      setError(null);

      try {
        const [docRes, userRes, storageRes] = await Promise.all([
          platformAnalytics.getDocumentStats(),
          platformAnalytics.getUserStats(),
          platformAnalytics.getStorageStats(),
        ]);

        if (docRes.success && docRes.data) {
          setDocumentStats(docRes.data);
        }
        if (userRes.success && userRes.data) {
          setUserStats(userRes.data);
        }
        if (storageRes.success && storageRes.data) {
          setStorageStats(storageRes.data);
        }
      } catch (err) {
        setError('Failed to load analytics data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchAnalytics();
  }, []);

  function formatStorage(mb: number): string {
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(2)} GB`;
    }
    return `${mb.toFixed(2)} MB`;
  }

  function getMaxValue(items: { count?: number; usedMb?: number }[]): number {
    return Math.max(...items.map(i => i.count ?? i.usedMb ?? 0), 1);
  }

  const tabs = [
    { id: 'overview' as const, label: 'Overview' },
    { id: 'documents' as const, label: 'Documents' },
    { id: 'users' as const, label: 'Users' },
    { id: 'storage' as const, label: 'Storage' },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Platform Analytics</h1>
        <p className={styles.subtitle}>Monitor platform usage and performance metrics</p>
      </div>

      {error && <div className={styles.errorBanner}>{error}</div>}

      <div className={styles.tabs}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.active : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className={styles.loading}>Loading analytics...</div>
      ) : (
        <>
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className={styles.content}>
              <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                  <div className={styles.statIcon} style={{ backgroundColor: 'rgba(0, 107, 63, 0.1)', color: 'var(--ghana-green)' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4C4 2.89543 4.89543 2 6 2H14L20 8V20C20 21.1046 19.1046 22 18 22H6C4.89543 22 4 21.1046 4 20V4Z" />
                      <path d="M14 2V8H20" />
                    </svg>
                  </div>
                  <div className={styles.statContent}>
                    <span className={styles.statValue}>{documentStats?.totalDocuments ?? 0}</span>
                    <span className={styles.statLabel}>Total Documents</span>
                  </div>
                </div>

                <div className={styles.statCard}>
                  <div className={styles.statIcon} style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', color: 'var(--info)' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" />
                      <circle cx="9" cy="7" r="4" />
                    </svg>
                  </div>
                  <div className={styles.statContent}>
                    <span className={styles.statValue}>{userStats?.totalUsers ?? 0}</span>
                    <span className={styles.statLabel}>Total Users</span>
                  </div>
                </div>

                <div className={styles.statCard}>
                  <div className={styles.statIcon} style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)', color: 'var(--success)' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12,6 12,12 16,14" />
                    </svg>
                  </div>
                  <div className={styles.statContent}>
                    <span className={styles.statValue}>{userStats?.activeUsers ?? 0}</span>
                    <span className={styles.statLabel}>Active Users</span>
                  </div>
                </div>

                <div className={styles.statCard}>
                  <div className={styles.statIcon} style={{ backgroundColor: 'rgba(252, 209, 22, 0.1)', color: 'var(--ghana-gold)' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                    </svg>
                  </div>
                  <div className={styles.statContent}>
                    <span className={styles.statValue}>{formatStorage(storageStats?.totalStorageUsedMb ?? 0)}</span>
                    <span className={styles.statLabel}>Storage Used</span>
                  </div>
                </div>
              </div>

              <div className={styles.chartsGrid}>
                <div className={styles.chartCard}>
                  <h3 className={styles.chartTitle}>Document Activity</h3>
                  <div className={styles.activityStats}>
                    <div className={styles.activityItem}>
                      <span className={styles.activityValue}>{documentStats?.documentsCreatedToday ?? 0}</span>
                      <span className={styles.activityLabel}>Today</span>
                    </div>
                    <div className={styles.activityItem}>
                      <span className={styles.activityValue}>{documentStats?.documentsCreatedThisWeek ?? 0}</span>
                      <span className={styles.activityLabel}>This Week</span>
                    </div>
                    <div className={styles.activityItem}>
                      <span className={styles.activityValue}>{documentStats?.documentsCreatedThisMonth ?? 0}</span>
                      <span className={styles.activityLabel}>This Month</span>
                    </div>
                  </div>
                </div>

                <div className={styles.chartCard}>
                  <h3 className={styles.chartTitle}>Login Activity</h3>
                  <div className={styles.activityStats}>
                    <div className={styles.activityItem}>
                      <span className={styles.activityValue}>{userStats?.loginsToday ?? 0}</span>
                      <span className={styles.activityLabel}>Today</span>
                    </div>
                    <div className={styles.activityItem}>
                      <span className={styles.activityValue}>{userStats?.loginsThisWeek ?? 0}</span>
                      <span className={styles.activityLabel}>This Week</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className={styles.content}>
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Documents by Type</h3>
                {documentStats?.documentsByType && documentStats.documentsByType.length > 0 ? (
                  <div className={styles.barChart}>
                    {documentStats.documentsByType.map((item) => (
                      <div key={item.typeId} className={styles.barRow}>
                        <span className={styles.barLabel}>{item.typeName}</span>
                        <div className={styles.barContainer}>
                          <div
                            className={styles.bar}
                            style={{
                              width: `${(item.count / getMaxValue(documentStats.documentsByType)) * 100}%`,
                              backgroundColor: 'var(--ghana-green)',
                            }}
                          />
                        </div>
                        <span className={styles.barValue}>{item.count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.emptyState}>No document type data available</div>
                )}
              </div>

              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Documents by Status</h3>
                {documentStats?.documentsByStatus && documentStats.documentsByStatus.length > 0 ? (
                  <div className={styles.barChart}>
                    {documentStats.documentsByStatus.map((item) => (
                      <div key={item.statusId} className={styles.barRow}>
                        <span className={styles.barLabel}>{item.statusName}</span>
                        <div className={styles.barContainer}>
                          <div
                            className={styles.bar}
                            style={{
                              width: `${(item.count / getMaxValue(documentStats.documentsByStatus)) * 100}%`,
                              backgroundColor: 'var(--info)',
                            }}
                          />
                        </div>
                        <span className={styles.barValue}>{item.count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.emptyState}>No document status data available</div>
                )}
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className={styles.content}>
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Users by Institution</h3>
                {userStats?.usersByMda && userStats.usersByMda.length > 0 ? (
                  <div className={styles.barChart}>
                    {userStats.usersByMda.map((item) => (
                      <div key={item.mdaId} className={styles.barRow}>
                        <span className={styles.barLabel}>{item.mdaName}</span>
                        <div className={styles.barContainer}>
                          <div
                            className={styles.bar}
                            style={{
                              width: `${(item.count / getMaxValue(userStats.usersByMda)) * 100}%`,
                              backgroundColor: 'var(--ghana-green)',
                            }}
                          />
                        </div>
                        <span className={styles.barValue}>{item.count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.emptyState}>No user distribution data available</div>
                )}
              </div>

              <div className={styles.summaryCards}>
                <div className={styles.summaryCard}>
                  <span className={styles.summaryValue}>{userStats?.totalUsers ?? 0}</span>
                  <span className={styles.summaryLabel}>Total Registered Users</span>
                </div>
                <div className={styles.summaryCard}>
                  <span className={styles.summaryValue}>{userStats?.activeUsers ?? 0}</span>
                  <span className={styles.summaryLabel}>Active Users (30 days)</span>
                </div>
                <div className={styles.summaryCard}>
                  <span className={styles.summaryValue}>
                    {userStats?.totalUsers ? ((userStats.activeUsers / userStats.totalUsers) * 100).toFixed(1) : 0}%
                  </span>
                  <span className={styles.summaryLabel}>Activation Rate</span>
                </div>
              </div>
            </div>
          )}

          {/* Storage Tab */}
          {activeTab === 'storage' && (
            <div className={styles.content}>
              <div className={styles.storageOverview}>
                <div className={styles.storageTotal}>
                  <span className={styles.storageTotalValue}>
                    {formatStorage(storageStats?.totalStorageUsedMb ?? 0)}
                  </span>
                  <span className={styles.storageTotalLabel}>Total Storage Used</span>
                </div>
              </div>

              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Storage by Institution</h3>
                {storageStats?.storageByMda && storageStats.storageByMda.length > 0 ? (
                  <div className={styles.barChart}>
                    {storageStats.storageByMda.map((item) => (
                      <div key={item.mdaId} className={styles.barRow}>
                        <span className={styles.barLabel}>{item.mdaName}</span>
                        <div className={styles.barContainer}>
                          <div
                            className={styles.bar}
                            style={{
                              width: `${(item.usedMb / getMaxValue(storageStats.storageByMda)) * 100}%`,
                              backgroundColor: 'var(--ghana-gold)',
                            }}
                          />
                        </div>
                        <span className={styles.barValue}>{formatStorage(item.usedMb)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.emptyState}>No storage data available</div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
